import React, { useState, useRef, useEffect } from 'react';
import FullScreenImageViewer from '../FullScreenImageViewer/FullScreenImageViewer';
import './ImageCarousel.css';

const ImageCarousel = ({ images = [], alt = 'Item' }) => {
    const validImages = (images || []).filter(Boolean);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const touchStartX = useRef(null);

    useEffect(() => {
        setActiveIndex((prev) => (prev >= validImages.length ? 0 : prev));
    }, [validImages.length]);

    if (validImages.length === 0) return null;

    if (validImages.length === 1) {
        return (
            <>
                <img
                    src={validImages[0]}
                    alt={alt}
                    className="image-carousel-single"
                    onClick={() => setIsFullScreen(true)}
                />
                <FullScreenImageViewer
                    imageSrc={validImages[0]}
                    isOpen={isFullScreen}
                    onClose={() => setIsFullScreen(false)}
                />
            </>
        );
    }

    const goTo = (index) => {
        if (index < 0 || index >= validImages.length) return;
        setActiveIndex(index);
    };

    const handleTouchStart = (e) => {
        touchStartX.current = e.changedTouches?.[0]?.clientX ?? null;
    };

    const handleTouchEnd = (e) => {
        if (touchStartX.current == null) return;
        const endX = e.changedTouches?.[0]?.clientX ?? touchStartX.current;
        const delta = endX - touchStartX.current;
        touchStartX.current = null;
        if (Math.abs(delta) < 40) return;
        if (delta < 0) goTo(activeIndex + 1);
        else goTo(activeIndex - 1);
    };

    return (
        <div className="image-carousel">
            <div
                className="image-carousel-viewport"
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
            >
                <div
                    className="image-carousel-track"
                    style={{ transform: `translateX(-${activeIndex * 100}%)` }}
                >
                    {validImages.map((src, index) => (
                        <div className="image-carousel-slide" key={`${src}-${index}`}>
                            <img
                                src={src}
                                alt={`${alt} ${index + 1}`}
                                onClick={() => setIsFullScreen(true)}
                            />
                        </div>
                    ))}
                </div>

                {activeIndex > 0 && (
                    <button
                        type="button"
                        className="image-carousel-nav image-carousel-nav--prev"
                        onClick={() => goTo(activeIndex - 1)}
                        aria-label="Previous image"
                    >
                        ‹
                    </button>
                )}
                {activeIndex < validImages.length - 1 && (
                    <button
                        type="button"
                        className="image-carousel-nav image-carousel-nav--next"
                        onClick={() => goTo(activeIndex + 1)}
                        aria-label="Next image"
                    >
                        ›
                    </button>
                )}
            </div>

            <div className="image-carousel-dots" role="tablist" aria-label="Image slides">
                {validImages.map((_, index) => (
                    <button
                        key={index}
                        type="button"
                        className={`image-carousel-dot${index === activeIndex ? ' is-active' : ''}`}
                        onClick={() => goTo(index)}
                        aria-label={`Show image ${index + 1}`}
                        aria-selected={index === activeIndex}
                    />
                ))}
            </div>

            <FullScreenImageViewer
                imageSrc={validImages[activeIndex]}
                isOpen={isFullScreen}
                onClose={() => setIsFullScreen(false)}
            />
        </div>
    );
};

export default ImageCarousel;
